import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import { syncStripePaymentToBackend } from "./stripePaymentBackend";

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let cancelled = false;
    const query = new URLSearchParams(location.search);

    const run = async () => {
      sessionStorage.removeItem("bookingApisCompleted");

      const statePi = location.state?.paymentIntent;
      if (statePi && !cancelled) {
        await syncStripePaymentToBackend(statePi, { paymentStatusId: 2 });
      }

      const clientSecret =
        query.get("payment_intent_client_secret") ||
        query.get("setup_intent_client_secret");

      const pk = sessionStorage.getItem("stripePublishableKey");
      if (!pk) {
        const piIdFallback = query.get("payment_intent") || "";
        if (piIdFallback && !cancelled) {
          await syncStripePaymentToBackend(null, {
            paymentIntentId: piIdFallback,
            paymentStatusId: 2,
          });
        }
        setStatus("success");
        if (!sessionStorage.getItem("paymentSlipOpened")) {
          sessionStorage.setItem("paymentSlipOpened", "1");
          window.open("/paymentslip", "_blank");
        }
        return;
      }

      if (!clientSecret) {
        const piIdFallback = query.get("payment_intent") || "";
        if (piIdFallback && !cancelled) {
          await syncStripePaymentToBackend(null, {
            paymentIntentId: piIdFallback,
            paymentStatusId: 2,
          });
        }
        setStatus("success");
        if (!sessionStorage.getItem("paymentSlipOpened")) {
          sessionStorage.setItem("paymentSlipOpened", "1");
          window.open("/paymentslip", "_blank");
        }
        return;
      }

      try {
        const stripe = await loadStripe(pk);
        if (!stripe || cancelled) return;

        const { paymentIntent } =
          await stripe.retrievePaymentIntent(clientSecret);
        if (cancelled) return;
        if (paymentIntent?.status === "succeeded") {
          await syncStripePaymentToBackend(paymentIntent, {
            paymentIntentId: query.get("payment_intent") || "",
            paymentStatusId: 2,
          });
          setStatus("success");
          if (!sessionStorage.getItem("paymentSlipOpened")) {
            sessionStorage.setItem("paymentSlipOpened", "1");
            window.open("/paymentslip", "_blank");
          }
        } else {
          setStatus("failed");
          navigate("/payment-failed", {
            replace: true,
            state: { paymentIntent },
          });
        }
      } catch {
        setStatus("success");
        if (!sessionStorage.getItem("paymentSlipOpened")) {
          sessionStorage.setItem("paymentSlipOpened", "1");
          window.open("/paymentslip", "_blank");
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [navigate, location.search, location.state]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-emerald-50 via-white to-teal-100 relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(16, 185, 129, 0.25) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(20, 184, 166, 0.2) 0%, transparent 50%)",
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="rounded-3xl bg-white/90 backdrop-blur-sm shadow-2xl shadow-emerald-900/10 border border-emerald-100/80 p-8 sm:p-10 text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 shadow-lg shadow-emerald-500/35 ring-4 ring-emerald-200/60">
            <FaCheckCircle className="h-10 w-10 text-white" aria-hidden />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600/90 mb-2">
            All set
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Payment successful
          </h1>
          <p className="mt-4 text-gray-600 leading-relaxed">
            {status === "checking"
              ? "We’re confirming your payment with Stripe. This only takes a moment."
              : "Thank you. Your transaction completed successfully and your booking is secured."}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-md shadow-orange-500/25 transition-all duration-200 hover:shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5"
              onClick={() => window.open("/paymentslip", "_blank")}
            >
              View payment slip
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              onClick={() => navigate("/", { replace: true })}
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
